import { Routes } from '@angular/router';
import { AuthGuard } from './shared/guard/auth.guard';
import { UserGuard } from './shared/guard/user.guard';
import { SuperAdminGuard } from './shared/guard/super-admin.guard';
import { GuestGuard } from './shared/guard/guest.guard';
import { ClientListComponent } from './client-list/client-list.component';
import { AdminGuard } from './shared/guard/admin.guard';
import { AccessControlGuard } from './shared/guard/access-control.guard';
import { ConsoleLogGuard } from './shared/guard/console-log.guard';
import { TemplateModalComponent } from './shared/components/template-modal/template-modal.component';
// SCHEDULE TEMPLATES DISABLED: import commented out with the route below, so the
// component is no longer pulled into the bundle. Restore both together.
// import { YearlyScheduleTemplateComponent } from './yearly-schedule-template/yearly-schedule-template.component';
import { DynamicFormUIComponent } from './shared/components/dynamic-form-ui/dynamic-form-ui.component';
import { DynamicFormModelComponent } from './dynamic-form-model/dynamic-form-model.component';
import { DWSiteListComponent } from './dw-site-list/dw-site-list.component';
import { MemoryStatusComponent } from './shared/components/memory-status/memory-status.component';

export const routes: Routes = [
  {
    path: '',
    redirectTo: 'login',
    pathMatch: 'full'
  },
  {
    path: 'login',
    loadComponent: () => import(`./auth/login/login.component`)
      .then(mod => mod.LoginComponent),
    canActivate: [GuestGuard]
  },
  {
    path: 'forgot-password',
    loadComponent: () => import(`./auth/forgot-password/forgot-password.component`)
      .then(mod => mod.ForgotPasswordComponent),
    canActivate: [GuestGuard]
  },
  {
    path: 'set-new-password/:email',
    loadComponent: () => import(`./auth/set-new-password/set-new-password.component`)
      .then(mod => mod.SetNewPasswordComponent),
    canActivate: [GuestGuard]
  },
  {
    path: 'verify-code/:email',
    loadComponent: () => import(`./auth/verification-code/verification-code.component`)
      .then(mod => mod.VerificationCodeComponent),
    canActivate: [GuestGuard]
  },
  {
    path: 'dashboard',
    loadComponent: () => import(`./tab-dashboard/tab-dashboard.component`)
      .then(mod => mod.TabDashboardComponent),
    canActivate: [AuthGuard, UserGuard]
  },
  {
    path: 'site-monitors',
    loadComponent: () => import(`./site-monitors/site-monitors.component`)
      .then(mod => mod.SiteMonitorsComponent),
    canActivate: [AuthGuard, UserGuard]
  },
  {
    path: 'site-monitors/:id',
    loadComponent: () => import(`./site-monitors/site-monitors-details/site-monitors-details.component`)
      .then(mod => mod.SiteMonitorsDetailsComponent),
    canActivate: [AuthGuard, UserGuard]
  },
  {
    path: 'new-dashboard',
    loadComponent: () => import(`./new-dashboard/new-dashboard.component`)
      .then(mod => mod.NewDashboardComponent),
    canActivate: [AuthGuard]
  },
  {
    path: 'users',
    loadChildren: () => import('./users/users.routes')
      .then(routes => routes.routes)
  },
  {
    path: 'sites',
    loadChildren: () => import('./site-list/site-list.routes')
      .then(routes => routes.routes)
  },
  {
    path: 'user-setting/site-content',
    loadComponent: () => import(`./user-setting/privacy-policy/privacy-policy.component`)
      .then(mod => mod.PrivacyPolicyComponent),
    canActivate: [AuthGuard]
  },
  {
    path: 'content/:type',
    loadComponent: () => import(`./content/content.component`)
      .then(mod => mod.ContentComponent),
    canActivate: [AuthGuard]
  },
  {
    path: 'role',
    loadComponent: () => import(`./role/role.component`)
      .then(mod => mod.RoleComponent),
    canActivate: [AuthGuard, SuperAdminGuard]
  },
  {
    path: 'client-settings',
    loadChildren: () => import('./settings/settings.routes')
      .then(routes => routes.routes),
    canActivate: [AuthGuard]
  },
  {
    path: 'role',
    loadChildren: () => import('./site-list/site-list.routes')
      .then(routes => routes.routes)
  },
  {
    path: 'settings',
    loadChildren: () => import('./settings/settings.routes')
      .then(routes => routes.routes),
    canActivate: [AuthGuard]
  },
  {
    path: 'client-support',
    loadChildren: () => import('./client-support-role/client-support-role.routes')
      .then(routes => routes.routes),
    canActivate: [AuthGuard]
  },
  {
    path: 'site-admins',
    loadComponent: () => import(`./super-admin/super-admins/super-admins.component`)
      .then(mod => mod.SuperAdminsComponent),
    canActivate: [AuthGuard, SuperAdminGuard]
  },
  // SCHEDULE TEMPLATES DISABLED — the day-wise hour-grid feature is switched off; team
  // and site schedules are the weekly arm/disarm plan only. Route left here to restore.
  // {
  //   path: 'templates',
  //   loadComponent: () => import(`./templates/templates.component`)
  //     .then(mod => mod.TemplatesComponent),
  //   canActivate: [AuthGuard]
  // },
  {
    path: 'speaker',
    loadComponent: () => import(`./speaker/speaker.component`)
      .then(mod => mod.SpeakerComponent),
    canActivate: [AuthGuard]
  },
  {
    path: 'call-police',
    loadComponent: () => import(`./call-police/call-police.component`)
      .then(mod => mod.CallPoliceComponent),
    canActivate: [AuthGuard]
  },
  {
    path: 'alerts',
    loadComponent: () => import(`./users/alerts/alerts.component`)
      .then(mod => mod.AlertsComponent),
    canActivate: [AuthGuard]
  },
  {
    path: 'clients',
    component: ClientListComponent,
    canActivate: [AdminGuard, SuperAdminGuard]
  },
  // SCHEDULE TEMPLATES DISABLED — see the 'templates' route above.
  // {
  //   path: 'templates/yearly-schedule',
  //   component: YearlyScheduleTemplateComponent,
  //   canActivate: [AuthGuard]
  // },
  {
    path: 'backup',
    loadComponent: () => import(`./backup/backup.component`)
      .then(mod => mod.BackupComponent),
    canActivate: [AuthGuard]
  },
  {
    path: 'dynamic-form-add',
    component: DynamicFormModelComponent,
    canActivate: [AdminGuard, SuperAdminGuard]
  },
  {
    path: 'Dynamic-Form',
    component: DynamicFormUIComponent,
    canActivate: [AdminGuard, SuperAdminGuard]
  },
  {
    path: 'group',
    loadComponent: () => import(`./group/group.component`)
      .then(mod => mod.GroupComponent),
    canActivate: [AuthGuard]
  },
  {
    path: 'user-logs',
    loadComponent: () => import(`./user-logs/user-logs.component`)
      .then(mod => mod.UserLogsComponent),
    canActivate: [AuthGuard]
  },
  {
    path: 'console-log',
    loadComponent: () => import(`./console-log/console-log.component`)
      .then(mod => mod.ConsoleLogComponent),
    canActivate: [AuthGuard, ConsoleLogGuard]
  },
  {
    path: 'unknown-site',
    loadComponent: () => import(`./unknown-site-alert/unknown-site-alert.component`)
      .then(mod => mod.UnknownSiteAlertComponent),
    canActivate: [AuthGuard, ConsoleLogGuard]
  },
  {
    path: 'alert-message-info',
    loadComponent: () => import(`./alert-message-info/alert-message-info.component`)
      .then(mod => mod.AlertMessageInfoComponent),
    canActivate: [AuthGuard]
  },
  {
    path: 'incident-reports',
    loadComponent: () => import('./incident-reports/incident-reports.component')
      .then((mod) => mod.IncidentReportsComponent),
    canActivate: [AuthGuard]
  },
  {
    path: 'DW-site-list',
    loadComponent: () => import(`./dw-site-list/dw-site-list.component`)
      .then(mod => mod.DWSiteListComponent),
  }, 
  {
    path: 'inactive-sites',
    loadComponent: () => import(`./inactive-sites/inactive-sites.component`)
      .then(mod => mod.InactiveSitesComponent),
    canActivate: [AuthGuard]
  },
  {
    path: 'memory-status',
    loadComponent: () => import(`./shared/components/memory-status/memory-status.component`)
      .then(mod => mod.MemoryStatusComponent),
    canActivate: [AuthGuard]
  },
  {
    path: 'reports',
    loadComponent: () => import(`./alert-chart-reports/alert-chart-reports.component`)
      .then(mod => mod.AlertChartReportsComponent),
    canActivate: [AuthGuard]
  },
  {
    path: 'manager-groups',
    loadComponent: () => import(`./manager-groups/manager-groups.component`)
      .then(mod => mod.ManagerGroupsComponent),
    canActivate: [AuthGuard]
  },
  {
    // Hidden admin-only page (not shown in the sidebar) to manage tabs + role permissions.
    path: 'access-control',
    loadComponent: () => import(`./access-control/access-control.component`)
      .then(mod => mod.AccessControlComponent),
    canActivate: [AuthGuard, AccessControlGuard]
  },
  {
    // Hidden page (not shown in the sidebar): reached by typing /last-login.
    path: 'last-login',
    loadComponent: () => import(`./last-login/last-login.component`)
      .then(mod => mod.LastLoginComponent),
    canActivate: [AuthGuard, AccessControlGuard]
  },
  {
    path: 'chat',
    loadChildren: () => import(`./chat/chat.routes`)
      .then(mod => mod.CHAT_ROUTES),
    canActivate: [AuthGuard, UserGuard]
  },
  {
    path: '**',
    loadComponent: () => import(`./page-not-found/page-not-found.component`)
      .then(mod => mod.PageNotFoundComponent),
    canActivate: [AuthGuard]
  },
];
